import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import {
  listTaxRules,
  getTaxRule,
  createTaxRule,
  updateTaxRule,
  deactivateTaxRule,
  createExchangeRate,
} from '@/api/services/tax-rules.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { isConflictError } from '@/features/tax-rules/utils/taxRuleFieldErrors'
import type {
  TaxRuleListFilters,
  CreateTaxRuleDto,
  UpdateTaxRuleDto,
  CreateExchangeRateDto,
} from '@/features/tax-rules/types/tax-rule.types'

export const taxRuleKeys = {
  all: () => ['tax-rules'] as const,
  list: (filters?: TaxRuleListFilters) => ['tax-rules', 'list', filters] as const,
  detail: (id: string | null) => ['tax-rules', 'detail', id] as const,
}

// ─── Queries ───────────────────────────────────────

export function useTaxRules(filters?: TaxRuleListFilters) {
  return usePermittedQuery({
    module: 'tax_rules',
    action: 'read',
    queryKey: taxRuleKeys.list(filters),
    queryFn: () => listTaxRules(filters),
    staleTime: 60_000,
  })
}

export function useTaxRule(id: string | null) {
  return usePermittedQuery({
    module: 'tax_rules',
    action: 'read',
    queryKey: taxRuleKeys.detail(id),
    queryFn: () => getTaxRule(id!),
    staleTime: 60_000,
    enabled: !!id,
  })
}

// ─── Mutations ─────────────────────────────────────

export function useCreateTaxRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTaxRuleDto) => createTaxRule(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxRuleKeys.all() })
      toast.success('Tax rule created.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useUpdateTaxRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTaxRuleDto }) => updateTaxRule(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: taxRuleKeys.all() })
      qc.invalidateQueries({ queryKey: taxRuleKeys.detail(id) })
      toast.success('Tax rule updated (new version created).')
    },
    // A 409 means a newer version exists; the form explains it with a link.
    onError: (error) => {
      if (isConflictError(error)) return
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeactivateTaxRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deactivateTaxRule(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: taxRuleKeys.all() })
      qc.invalidateQueries({ queryKey: taxRuleKeys.detail(id) })
      toast.success('Tax rule deactivated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useCreateExchangeRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateExchangeRateDto) => createExchangeRate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxRuleKeys.all() })
      toast.success('Exchange rate created.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
